import type { QueryClient } from '@tanstack/react-query'
import { isDocumentTranslated, MIN_WITNESS_LENGTH, wasOurTextReplaced } from './hydration-mismatch'

/**
 * Records text the browser environment replaces while React is hydrating, so a hydration mismatch can
 * be attributed to a page translator / content blocker / in-app-WebView injection instead of to us.
 *
 * WHY A LOG AND NOT A LOOK AT THE DOM. The DOM cannot answer "was this page rewritten?" at any single
 * moment. Before `hydrateRoot` is too early: hydration runs inside a `startTransition`, so it is
 * concurrent and a rewrite lands part-way through it. When the error is reported is too late: React's
 * recovery restores the very text you would compare against. A mutation record survives both, because
 * it captures the transition rather than the state.
 *
 * WHY AN INLINE SCRIPT AND NOT THIS MODULE. The client entry is `<script type="module" async>`, so on
 * the slow devices where this bug actually happens it executes AFTER the rewriter has finished — an
 * observer installed from the entry module records none of the rewrites it was built to catch. The
 * observer therefore has to be armed by a classic inline script in `<head>`, which the parser runs
 * before any module. Same reason `initGlobalsScript` is inlined in `__root.tsx`, and the constraint
 * from that file applies here too: the text must be a CONSTANT, byte-identical on both sides, or
 * TanStack's script dedup fails and the whole bootstrap is re-executed on every hydration.
 *
 * WHAT IS DELIBERATELY NOT WATCHED. `childList` is off. Its only use would be spotting the `<font>`
 * wrappers desktop Google Translate inserts, and the wave this was built for produces none (it rewrites
 * text in place). Subscribing would mean work for every node the parser appends, on precisely the
 * low-end devices that lose the hydration race to begin with — all cost, no signal.
 */

/** Where the inline recorder parks its findings. Read only through {@link recordedTextReplacements}. */
const REWRITE_LOG_KEY = '__dcTextRewrites'

/**
 * Raised by the recorder when it hits {@link MAX_RECORDS} and disconnects. Without it, a log that
 * stopped recording and a page that stopped mutating are the same silence — `replacements=` in the
 * attribution would read as a total when it is only a floor, on exactly the churn-heavy pages most
 * likely to bury a real rewrite past the cap.
 */
const REWRITE_LOG_CAPPED_KEY = '__dcTextRewritesCapped'

/**
 * Raised by {@link markHydrationStarted} the instant before `hydrateRoot`, and stamped onto every
 * record. A replacement logged while this was still down provably predates any React DOM write, so it
 * cannot be React repairing a mismatch — it is the one class of evidence that needs no witness at all.
 *
 * Only counted, not yet acted on: the measured wave rewrites *before* hydration, but hydration is
 * concurrent and a rewrite can also land mid-flight, so narrowing the gate to pre-hydration records
 * could disable the attribution on devices this cannot be re-measured against. The counts ride in the
 * report (see {@link describeHydrationRewrite}) so the next real issue settles it with data.
 */
const HYDRATION_STARTED_KEY = '__dcHydrating'

/**
 * Bounded so a runaway loop or a hostile script cannot grow the log without limit on a phone. Set well
 * above what a real page produces — most of what a page logs is React's own Suspense placeholders, and
 * the cap must never be reached before a rewrite is seen. When it IS reached the recorder raises
 * {@link REWRITE_LOG_CAPPED_KEY} on its way out, so the report can say the log is a floor, not a total.
 */
const MAX_RECORDS = 300

/** Enough for a witness to still match on its prefix; short enough that a page-sized node cannot bloat the log. */
const MAX_VALUE_LENGTH = 512

/** Hydration is over long before this; afterwards ordinary DOM churn must not be able to silence a report. */
const WATCH_WINDOW_MS = 10_000

/**
 * The inline recorder. Deliberately DUMB: it records every `characterData` mutation verbatim and makes
 * no judgements, so the rules about what counts as a foreign rewrite live in
 * {@link isForeignTextReplacement} — one tested implementation rather than a copy stranded inside a
 * string. Kept as a single constant so the emitted text is identical on server and client.
 *
 * The one thing it adds beyond a verbatim record is a per-node id (`d`), because node identity cannot
 * be recovered from the strings later: `nodeValue` is sampled when the observer callback FLUSHES, so
 * every record one node produced within a batch carries the batch's final value as its `after`. The id
 * is what lets {@link recordedTextReplacements} rebuild each record's true `after` — in tested code,
 * where that rule belongs — from the next same-node record's `before`.
 */
export const HYDRATION_REWRITE_MONITOR_SCRIPT =
  `(function(){try{` +
  `var K='${REWRITE_LOG_KEY}',L=window[K]=window[K]||[];` +
  `if(typeof MutationObserver==='undefined')return;` +
  `var M=new WeakMap(),n=0;` +
  `var o=new MutationObserver(function(rs){for(var i=0;i<rs.length;i++){` +
  `if(L.length>=${MAX_RECORDS}){window['${REWRITE_LOG_CAPPED_KEY}']=1;o.disconnect();return}` +
  `var r=rs[i];if(r.type!=='characterData')continue;var t=r.target;` +
  `var d=M.get(t);if(d==null)M.set(t,d=n++);` +
  `L.push([t.nodeType,(r.oldValue==null?'':r.oldValue).slice(0,${MAX_VALUE_LENGTH}),(t.nodeValue==null?'':t.nodeValue).slice(0,${MAX_VALUE_LENGTH}),window['${HYDRATION_STARTED_KEY}']?0:1,d])` +
  `}});` +
  `o.observe(document,{characterData:true,characterDataOldValue:true,subtree:true});` +
  `setTimeout(function(){try{o.disconnect()}catch(e){}},${WATCH_WINDOW_MS})` +
  `}catch(e){}})();`

/** React's Suspense streaming placeholders are COMMENT nodes; only real text can be a rewrite. */
const TEXT_NODE = 3

/**
 * Whether one recorded `characterData` mutation is a foreign rewrite, as opposed to React's or the
 * parser's own bookkeeping. Pure and exported so the two exclusions that matter are covered by tests
 * rather than by a DOM harness this project has no unit environment for.
 */
export function isForeignTextReplacement(nodeType: number, before: string, after: string): boolean {
  // Comments, not text: React rewrites its Suspense placeholders in place (`<!--$?-->` → `<!--$~-->`
  // → `<!--$-->`) and `characterData` fires for those too — on a route with several streamed
  // boundaries they outnumber the real rewrites several times over. Filtering on node type removes
  // every one, and is sturdier than matching the `$` sentinels, which are React's private encoding and
  // free to change.
  if (nodeType !== TEXT_NODE) return false
  if (before === after) return false
  // The parser delivers a streamed text node by APPENDING to it, firing `characterData` with
  // `oldValue` a prefix of the new value. That is the document arriving, not being rewritten.
  if (after.startsWith(before)) return false
  return true
}

/** A replacement as it comes out of the log, carrying when it happened as well as what it did. */
export interface RecordedTextReplacement {
  readonly before: string
  readonly after: string
  /** Whether it was logged before the app called `hydrateRoot`. See {@link HYDRATION_STARTED_KEY}. */
  readonly preHydration: boolean
}

/**
 * Raise the flag the recorder stamps onto records from here on. Called by the client entry immediately
 * before `hydrateRoot`, and nowhere else; guarded, because failing to mark must never cost the boot.
 */
export function markHydrationStarted(): void {
  try {
    if (typeof window === 'undefined') return
    ;(window as unknown as Record<string, unknown>)[HYDRATION_STARTED_KEY] = true
  } catch {
    // A frozen `window` costs the phase breakdown in the report, nothing else.
  }
}

/** The foreign text replacements recorded during hydration, for `wasOurTextReplaced`. */
export function recordedTextReplacements(): readonly RecordedTextReplacement[] {
  try {
    if (typeof window === 'undefined') return []
    const raw = (window as unknown as Record<string, unknown>)[REWRITE_LOG_KEY]
    if (!Array.isArray(raw)) return []
    const parsed: {
      nodeType: number
      before: string
      after: string
      preHydration: boolean
      nodeId: number | undefined
    }[] = []
    for (const entry of raw) {
      if (!Array.isArray(entry) || entry.length < 3) continue
      const [nodeType, before, after, preHydration, nodeId] = entry as [unknown, unknown, unknown, unknown, unknown]
      if (typeof nodeType !== 'number' || typeof before !== 'string' || typeof after !== 'string') continue
      parsed.push({
        nodeType,
        before,
        after,
        preHydration: preHydration === 1,
        // Missing on tuples from an older recorder: the record survives, only un-chained below.
        nodeId: typeof nodeId === 'number' ? nodeId : undefined,
      })
    }
    // The recorder samples `nodeValue` at callback-flush time, so every record one node produced within
    // a batch carries the batch's FINAL value as `after`. The true `after` of each record is the NEXT
    // same-node record's `before` — one node's mutations chain exactly, batches included —
    // reconstructed here in one backward pass. Without it, a translator rewrite that React repairs in
    // the SAME batch flattens to ours→ours and the rewrite disappears from the log: the repair keeps
    // reporting (fails open), but the attribution loses the very record that named the translator.
    const nextBeforeByNode = new Map<number, string>()
    for (let i = parsed.length - 1; i >= 0; i--) {
      const record = parsed[i]
      if (!record || record.nodeId === undefined) continue
      const nextBefore = nextBeforeByNode.get(record.nodeId)
      if (nextBefore !== undefined) record.after = nextBefore
      nextBeforeByNode.set(record.nodeId, record.before)
    }
    const replacements: RecordedTextReplacement[] = []
    for (const { nodeType, before, after, preHydration } of parsed) {
      if (isForeignTextReplacement(nodeType, before, after)) {
        replacements.push({ before, after, preHydration })
      }
    }
    return replacements
  } catch {
    return []
  }
}

/** Whether the recorder hit {@link MAX_RECORDS} and disconnected: the log is a floor, not a total. */
function wasRewriteLogCapped(): boolean {
  try {
    if (typeof window === 'undefined') return false
    return (window as unknown as Record<string, unknown>)[REWRITE_LOG_CAPPED_KEY] === 1
  } catch {
    return false
  }
}

/** Depth of the walk below. Query payloads here nest as `{ items: [{ title, description }] }`; three covers it. */
const MAX_WITNESS_DEPTH = 3

/** Bounded so harvesting witnesses off a large list response can never become the expensive part of a report. */
const MAX_WITNESSES = 60

/**
 * Strings the app can prove it rendered, harvested from the dehydrated query cache — the same data the
 * server built this document from. They are what `wasOurTextReplaced` matches recorded rewrites
 * against: a replacement that took one of these away and put something unaccounted for in its place was
 * not us.
 *
 * GENERIC, unlike the portals version it comes from, which read two named fields off the campaign
 * payload. There is no single entity here whose text dominates a page — a report, a job list and an
 * applicant profile each render prose from a different query — and rebuilding specific query keys would
 * couple the reporter to each one's input shape, where any drift silently returns nothing and disables
 * the attribution without failing anywhere. So: walk whatever is in the cache, shallowly, and take the
 * strings long enough to identify a node ({@link MIN_WITNESS_LENGTH}).
 *
 * Markup is stripped because some payload text is rendered as HTML, and the DOM node the monitor
 * recorded holds the TEXT. Fully guarded — losing a witness may cost an attribution, never a report.
 */
function renderedTextWitnesses(queryClient: QueryClient | undefined): string[] {
  const witnesses: string[] = []
  try {
    const cache = queryClient?.getQueryCache()
    if (!cache) return []
    const visit = (value: unknown, depth: number): void => {
      if (witnesses.length >= MAX_WITNESSES) return
      if (typeof value === 'string') {
        const text = value.replace(/<[^>]*>/g, ' ').trim()
        if (text.length >= MIN_WITNESS_LENGTH) witnesses.push(text)
        return
      }
      if (depth >= MAX_WITNESS_DEPTH || typeof value !== 'object' || value === null) return
      for (const child of Array.isArray(value) ? value : Object.values(value)) {
        visit(child, depth + 1)
        if (witnesses.length >= MAX_WITNESSES) return
      }
    }
    for (const query of cache.getAll()) {
      visit(query.state.data, 0)
      if (witnesses.length >= MAX_WITNESSES) break
    }
  } catch {
    return witnesses
  }
  return witnesses
}

/** Whether a hydration-class error belongs to whatever rewrote this document, and why we think so. */
export interface HydrationRewriteVerdict {
  /** True when the mismatch is the rewriter's, not ours: skip the report. */
  readonly foreign: boolean
  /** The reasoning, for the report's `counters` field. Always present, even when nothing was found. */
  readonly attribution: string
}

/**
 * THE gate for hydration-class errors, and deliberately the only one. Every report path goes through
 * `logFrontendError`, which asks this once — the boundary, the two global handlers and the
 * `hydrateRoot` handlers in `client.tsx` therefore cannot drift apart on it, which is exactly what
 * happened on the portals when the second signal shipped to the client entry alone and the global
 * handlers kept ticketing text-only rewrites through a weaker gate.
 *
 * Two signals, cheapest first:
 *
 *   - `isDocumentTranslated` catches translators that announce themselves: Chrome/Edge's `translated-*`
 *     class, or a rewritten `<html lang>`.
 *   - `wasOurTextReplaced` catches the ones that do not. A text-only rewrite leaves `<html lang>` and
 *     the class untouched while still reproducing `#418 args[]=text` exactly, so the first signal is
 *     blind to it.
 *
 * Both fail open, so an unrecognised rewrite still reports.
 *
 * The attribution exists because "the page was pristine" and "the check never ran" are otherwise the
 * same silence, which is how this class of bug stays undiagnosable. `witnesses=0` says the cache walk
 * found nothing usable, and `replacements=0` on an issue that keeps recurring says the monitor is not
 * seeing the rewrite. `pre=` is how many landed before `hydrateRoot`, the records that need no witness
 * to be trusted (see {@link HYDRATION_STARTED_KEY}). `capped=1` says the recorder hit
 * {@link MAX_RECORDS} and stopped, so `replacements=` is a floor and a rewrite may have landed
 * unrecorded. Healthy numbers on a report that still arrived mean the mismatch really was ours.
 * (`witnesses=0` also shows, expectedly, on the earliest shell errors, which can fire before the router
 * has populated the query cache; that path fails open by design.)
 *
 * It rides in the report's `counters` field, which is free-form and outside the error fingerprint, so
 * it cannot open a fresh GitHub issue per value.
 */
export function describeHydrationRewrite(queryClient: QueryClient | undefined): HydrationRewriteVerdict {
  const witnesses = renderedTextWitnesses(queryClient)
  const replacements = recordedTextReplacements()
  const preHydration = replacements.filter((replacement) => replacement.preHydration).length
  return {
    foreign: isDocumentTranslated() || wasOurTextReplaced(replacements, witnesses),
    attribution: `rewriteCheck: witnesses=${witnesses.length} replacements=${replacements.length} pre=${preHydration} capped=${wasRewriteLogCapped() ? 1 : 0}`,
  }
}
